import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {WRTCProviderService} from '@app/modules/wrtc/services/providers/wrtc-provider';
import {AccessType, ConnectionStatus, ViewportTab} from '@app/modules/shared/data/constants';
import {Store} from '@ngxs/store';
import {NetworkQualityLevelChangedPayload} from '@app/models/types';
import {CommandService} from '@app/services/command.service';
import {ClassroomAction, ClassroomState, ClassroomStateModel} from '@app/state/classroom-state.service';
import {GameModeModel, GameModeState} from '@app/state/game-mode.state';
import {LocalUser, LocalUserState} from '@app/state/local-user.state';
import {CoreService} from '@app/services/core.service';
import {ChatService} from '@app/modules/chat/services/chat.service';
import {WrtcService} from '@app/modules/wrtc/services/wrtc.service';
import {RemoteParticipantInfo} from '@app/models/remote-participant-info';
import {
  RemoteUserAction,
  RemoteUserAdditionalInfoStateModel,
  RemoteUserInfoStateModel,
  RemoteUsersInfoState
} from '@app/state/remote-users.state';
import {Whiteboard, WhiteboardState} from '@app/state/whiteboard.state';
import {SharedHandlersService} from '@app/modules/shared/services/shared-handlers.service';
import {Message, MessageType} from '@app/models/message';
import {AssignmentState, AssignmentStateModel} from '@app/state/assignment.state';

@Component({
  selector: 'app-remote-student',
  templateUrl: './remote-student.component.html',
  styleUrls: ['./remote-student.component.css']
})
export class RemoteStudentComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('mediaWrapper') wrapper: ElementRef<HTMLElement>;
  @Input() public student: RemoteParticipantInfo;
  @Input() public userIndex: number;
  @Input() public studentsCount: number;
  @Input() public status: ConnectionStatus;
  private remoteUsers: { [key: number]: RemoteUserAdditionalInfoStateModel };
  public localUser: LocalUser;
  public gameMode = GameModeState.defaults;
  public whiteboard: Whiteboard;
  public assignment: AssignmentStateModel;
  public voiceMeterLevel: number;
  public studentIndividualEventEmitters: { voiceMeterLevelChanged: EventEmitter<number>; networkLevelChanged: EventEmitter<NetworkQualityLevelChangedPayload> };

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public AccessType = AccessType;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public RoomConnectionState = ConnectionStatus;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public ViewPortTabsIndexes = ViewportTab;

  public isCurrentUserOnline = true;
  public isPrivateTalkParticipant = false;
  public timer = {
    isRunning: false,
    valueInSeconds: 0,
    intervalId: null
  };

  public classroom: ClassroomStateModel;

  public ngOnChanges(changes: SimpleChanges) {
    switch (changes?.status?.currentValue) {
      case ConnectionStatus.PENDING: {
        this.setUpTimer();
        break;
      }
      case ConnectionStatus.ONLINE: {
        if (this.timer.isRunning) {
          this.dropTimer();
        }
        break;
      }
    }
  }

  constructor(
    private wrtcProviderService: WRTCProviderService,
    private store: Store,
    private sharedHandlerService: SharedHandlersService,
    private commandService: CommandService,
    private coreService: CoreService,
    private chatService: ChatService,
    private wrtcService: WrtcService,
  ) {
    this.listenStoreChanges();
  }

  public ngOnInit(): void {}

  public ngAfterViewInit(): void {
    this.initStudentEventEmitters();

    this.store.select(ClassroomState).subscribe(roomState => {
      this.classroom = roomState;
      this.isPrivateTalkParticipant = !!this.classroom.privateTalkInfo.participants[this.student.id];
    });

    this.coreService.afterResetEmitter$.subscribe(()=> {
      if (this.localUser.accessType === AccessType.TEACHER || this.localUser.accessType === AccessType.SUPPORT) {
        this.initStudentEventEmitters();
      }
    });
  }

  public initStudentEventEmitters(): void {
    this.wrtcProviderService.addRemoteParticipantInfo(this.student.id, this.student.accessType, this.wrapper.nativeElement);
    this.studentIndividualEventEmitters = this.wrtcProviderService.getStudentIndividualEventEmitters(this.student.id);

    this.studentIndividualEventEmitters.voiceMeterLevelChanged.subscribe(voiceMeterLevel => {
      this.voiceMeterLevel = voiceMeterLevel;
    });
    this.studentIndividualEventEmitters.networkLevelChanged.subscribe(eventInfo => {
      // console.log('networkLevelChanged changed to : ' + eventInfo.level);
      this.student.additionalInfo.networkQualityLevel = eventInfo.level;
    });
  }

  public ngOnDestroy(): void {
    this.wrtcProviderService.removeRemoteParticipantMediaWrappers(this.student.id);
    if (this.timer.isRunning) {
      this.dropTimer();
    }
  }

  private setUpTimer(): void {
    this.timer.isRunning = true;
    this.timer.intervalId = setInterval(() => {
      this.timer.valueInSeconds++;

      if (this.timer.valueInSeconds === 60) {
        this.dropTimer();
        this.store.dispatch(new RemoteUserAction.SetUserConnectionStatus(this.student.id, ConnectionStatus.OFFLINE));
        // this.coreService.userLogout(this.student.id);
      }
    }, 1000);
  }

  private dropTimer(): void {
    clearInterval(this.timer.intervalId);
    this.timer.intervalId = null;
    this.timer.isRunning = false;
    this.timer.valueInSeconds = 0;
  }

  public sendWakeUpNotificationToStudent(elem): void {
    const blinkClass = 'blink-eye';

    if (this.localUser.accessType !== AccessType.OBSERVER && !elem.classList.contains(blinkClass)) {
      elem.classList.add(blinkClass);
      setTimeout(() => {
        elem.classList.remove(blinkClass);
      }, 3000);

      this.wrtcService.sendWakeUpSignal(this.student.id).subscribe();
    }
  }

  public handleClassroomUnmuteButtonClick(): void {
    this.store.dispatch(new ClassroomAction.ToggleRoomMutedBecauseOfBlockedMicAnCam(false));
    this.wrtcService.dropRoomMuteBecauseOfBlockedMicAndCam();
    this.coreService.initApplauseAudio();
    CoreService.playTimerNotificationSound(true);
  }

  public cancelRaiseHand(): void {
    if (this.localUser.accessType === AccessType.TEACHER || this.localUser.accessType === AccessType.SUPPORT) {
      this.coreService.sendCancelRaiseHand(this.student.id).subscribe(() => {
        this.store.dispatch(new RemoteUserAction.RiseHand(this.student.id, false));
      });
    }
  }

  public manuallyHideUnansweredMessageIcon(): void {
    this.store.dispatch(new RemoteUserAction.SetHasUnansweredMessage(this.student.id, false));

    const messageModel = new Message();
    messageModel.senderId = this.localUser.id;
    messageModel.targetId = this.student.id;
    messageModel.messageType = MessageType.SEEN;
    this.chatService.sendMessage(messageModel).subscribe();
    this.chatService.sendClassWideMessage(messageModel).subscribe();
  }

  public toggleAudioMute(dropQuiteMode: boolean = true): void {
    if (this.localUser.accessType === AccessType.OBSERVER) {
      return;
    }

    if (dropQuiteMode && this.classroom.isClassroomMuted) {
      const usersToMute: { [key: number]: RemoteUserAdditionalInfoStateModel } = Object.assign({}, this.remoteUsers);
      delete usersToMute[this.student.id];

      this.wrtcService.sendUsersAudioMute(
        Object.keys(usersToMute).map((id: string) => parseInt(id))
      ).subscribe(() => {
        this.wrtcService.sendMuteRoom(false, false).subscribe(() => {
          if (this.student.additionalInfo.audio.isMuted) {
            this.wrtcService.sendAudioUnmute(this.student.id).subscribe();
          } else {
            this.wrtcService.sendAudioMute(this.student.id).subscribe();
          }
        });
      });
    } else {
      if (this.student.additionalInfo.audio.isMuted) {
        this.wrtcService.sendAudioUnmute(this.student.id).subscribe();
      } else {
        this.wrtcService.sendAudioMute(this.student.id).subscribe();
      }
    }
  }

  public toggleVideoMute(): void {
    if (this.localUser.accessType === AccessType.OBSERVER) {
      return;
    }
    if (this.student.additionalInfo.video.isMuted) {
      this.wrtcService.sendVideoUnmute(this.student.id).subscribe();
    } else {
      this.wrtcService.sendVideoMute(this.student.id).subscribe();
    }
    this.store.dispatch(new RemoteUserAction.SetUserVideoMute(this.student.id, !this.student.additionalInfo.video.isMuted));
  }

  public toggleJoinPrivateTalk(): void {
    if (this.classroom.privateTalkInfo.participants[this.student.id]) {
      /* exclude from private talk */
      if (Object.keys(this.classroom.privateTalkInfo.participants).length === 1) {
        this.wrtcService.recoverMuteState();
        this.store.dispatch(new ClassroomAction.EndPrivateTalk());
        this.wrtcService.endPrivateWhiteboard();
        this.wrtcService.sendEndPrivateTalk().subscribe();
      } else {
        this.wrtcService.recoverStudentMuteState(this.student.id);
        this.store.dispatch(new ClassroomAction.RemovePrivateTalkParticipant(this.student.id));
        this.wrtcService.sendExcludeFromPrivateTalk(this.student.id).subscribe();
      }
    } else {
      /* add to private talk */
      this.store.dispatch(new ClassroomAction.AddPrivateTalkParticipant(this.student.id, this.student.additionalInfo.audio.isMuted));
      this.wrtcService.sendPrivateTalk(this.student.id).subscribe();
      if (this.student.additionalInfo.audio.isMuted) {
        this.toggleAudioMute(false);
      }
    }
  }

  public isHandIconVisible(): boolean {
    return this.localUser.accessType !== AccessType.STUDENT && this.student.additionalInfo.isHandRisen;
  }

  private listenStoreChanges(): void {
    this.store.select(RemoteUsersInfoState).subscribe(({usersInfoById}: RemoteUserInfoStateModel) => {
      this.remoteUsers = usersInfoById;
    });
    this.store.select(GameModeState).subscribe((gameMode: GameModeModel) => {
      this.gameMode = gameMode;
    });
    this.store.select(LocalUserState).subscribe((localUser: LocalUser) => {
      this.localUser = localUser;
    });
    this.store.select(WhiteboardState).subscribe((whiteboard: Whiteboard) => {
      this.whiteboard = whiteboard;
    });
    this.store.select(AssignmentState).subscribe((assignment: AssignmentStateModel) => {
      this.assignment = assignment;
    });
    this.store.select(ClassroomState).subscribe((classroom: ClassroomStateModel) => {
      this.classroom = classroom;
    });
  }
}
